import React, { FC, useEffect, useState } from 'react';

interface StepperProps {
  steps: {
    id: number;
    title: string;
    description: string;
    image?: string;
    component?: React.ReactNode;
  }[];
  onComplete?: () => void;
}

export const ModernStepper: FC<StepperProps> = ({ steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        handleNext();
      } else if (event.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className='w-full max-w-2xl mx-auto px-2'>
      { /* ── Progress Header ── */ }
      <div className='flex flex-col items-center mb-5'>
        <div className='border-t border-slate-50 flex items-center w-full justify-between'>
          { ' ' }
          <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                currentStep === 0
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
              }`}
          >
            <svg
                className='w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5'
                fill='none'
                stroke='currentColor'
                strokeWidth={2.5}
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M15 19l-7-7 7-7'
              />
            </svg>
            Prev
          </button>{ ' ' }
          <div className='flex items-center gap-1.5 '>
            { steps.map((_, index) => (
              <React.Fragment key={index}>
                <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 ${
                      index < currentStep
                        ? 'bg-emerald-500 text-white'
                        : index === currentStep
                          ? 'bg-emerald-600 text-white ring-4 ring-emerald-50 shadow-sm'
                          : 'bg-slate-200 text-slate-500'
                    }`}
                >
                  { index < currentStep ? (
                    <svg
                        className='w-3.5 h-3.5'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={3}
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M5 13l4 4L19 7'
                      />
                    </svg>
                  ) : (
                    index + 1
                  ) }
                </div>
                { index < steps.length - 1 && (
                  <div
                      className={`h-[2px] w-4 rounded-full transition-all duration-500 ${
                        index < currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                      }`}
                  />
                ) }
              </React.Fragment>
            )) }
          </div>
          <button
              onClick={handleNext}
              className='flex items-center gap-1.5 px-5 py-2 bg-emerald-600 text-white rounded-lg text-[11px] font-black hover:bg-emerald-700 shadow-md shadow-emerald-100 transition-all active:scale-95 hover:shadow-lg uppercase tracking-wider'
          >
            { currentStep === steps.length - 1 ? 'Get Started' : 'Next' }
            { currentStep < steps.length - 1 && (
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2.5}
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M9 5l7 7-7 7'
                />
              </svg>
            ) }
          </button>
        </div>

        <div className='text-center px-4'>
          <p className='text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1'>
            Step { currentStep + 1 } of { steps.length }
          </p>
          <h3 className='text-sm font-bold text-slate-800 leading-tight'>
            { currentStepData.title }
          </h3>
        </div>
      </div>

      { /* ── Content Area ── */ }
      <div className='bg-white min-h-[280px] flex flex-col transition-all duration-300'>
        <div
            className='text-slate-500 leading-relaxed text-[11px] text-center mb-4 px-2'
            dangerouslySetInnerHTML={{ __html: currentStepData.description }}
        />
        <div className='flex-1 flex flex-col justify-center'>
          { currentStepData.component ? (
            <div className='animate-in fade-in slide-in-from-bottom-2 duration-500'>
              { currentStepData.component }
            </div>
          ) : currentStepData.image ? (
            <div className='flex justify-center animate-in zoom-in-95 duration-500'>
              <div className='bg-slate-50 rounded-xl p-3 border border-slate-100 w-full max-w-sm'>
                <img
                    src={currentStepData.image}
                    alt={currentStepData.title}
                    className='w-full max-h-[160px] rounded-lg object-contain mix-blend-multiply'
                />
              </div>
            </div>
          ) : null }
        </div>
      </div>
    </div>
  );
};

